import React, { useEffect, useState } from "react";
import { X, Check, Loader2 } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ChannelIcon, { CHANNEL_CONFIG } from "../ChannelIcon";

/**
 * Cambia la línea (conexión) por la que sale este ticket.
 *
 * Vivía como un <select> siempre visible en la cabecera del chat, donde
 * ocupaba sitio permanente para una acción que casi no se usa. Ahora se abre
 * desde el menú de los tres puntos.
 *
 * Las líneas se piden al abrir el modal, no al montar la cabecera: antes cada
 * ticket que se abría disparaba un GET /whatsapp/ que nadie miraba.
 */
const ChangeTicketLineModal = ({ modalOpen, onClose, ticket, lines }) => {
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(ticket?.whatsappId || "");

  useEffect(() => {
    if (modalOpen) setSelected(ticket?.whatsappId || "");
  }, [modalOpen, ticket?.whatsappId]);

  useEffect(() => {
    if (!modalOpen) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [modalOpen, onClose]);

  if (!modalOpen) return null;

  const handleSave = async () => {
    if (!selected || Number(selected) === Number(ticket?.whatsappId)) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await api.put(`/tickets/${ticket.id}`, { whatsappId: selected });
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-md mx-4 animate-in fade-in slide-in-from-bottom-2 duration-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            {i18n.t("ticketOptionsMenu.changeLine")}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 max-h-80 overflow-y-auto">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            {i18n.t("changeTicketLineModal.hint")}
          </p>
          <div className="flex flex-col gap-1.5">
            {lines.map((line) => {
              const isCurrent = Number(line.id) === Number(selected);
              return (
                <button
                  key={line.id}
                  onClick={() => setSelected(line.id)}
                  className={`flex items-center gap-2.5 w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
                    isCurrent
                      ? "border-indigo-400 bg-indigo-50 dark:border-indigo-600 dark:bg-indigo-950/30"
                      : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                  }`}
                >
                  <span className="flex-shrink-0">
                    <ChannelIcon channel={line.channel} size={16} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                      {line.name}
                    </span>
                    <span className="block text-[11px] text-slate-400">
                      {CHANNEL_CONFIG[line.channel]?.label || line.channel}
                    </span>
                  </span>
                  {isCurrent && <Check className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            {i18n.t("confirmationModal.buttons.cancel")}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {i18n.t("changeTicketLineModal.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeTicketLineModal;
